// #region imports

// --- Angular
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

// --- RxJs
import { Observable, of } from 'rxjs';

// --- 3rd Party
import { TranslateService } from '@ngx-translate/core';
import { retryBackoff } from 'backoff-rxjs';

// --- HS Code
import { AppService } from '../app.service';
import { SurveyBaseService } from './survey-base.service';
import {
  SortDirections,
  SurveyModel,
  SurveySummaryModel,
  ApplicationError,
  PatchModel,
  NotificationTypes
} from '../../../models';
import { environment } from '../../../../environments/environment';
import { catchError, tap, switchMap } from 'rxjs/operators';
import { PatchOperations } from 'src/app/models/common/patch-operations';
import { ResponseBaseService } from './response-base.service';
import { ResultsBaseService } from '../results/results-base.service';
import { UserNotificationBaseService } from '../notifications/user-notification-base.service';

// #endregion

@Injectable({
  providedIn: 'root'
})
export class SurveyService extends SurveyBaseService {
  constructor(
    private translate: TranslateService,
    private http: HttpClient,
    protected resultsService: ResultsBaseService,
    protected appService: AppService,
    protected responseService: ResponseBaseService,
    protected notificationService : UserNotificationBaseService,
  ) {
    super(appService, responseService, resultsService, notificationService);

    this.notificationService.notifications$.subscribe(notifications => {
      if (!notifications) {
        return;
      }

      const survey = this._selectedSelfSurvey$.value;
      const surveys = this._selfSurveys$.value;
      notifications.forEach(element => {
        switch (element.type) {
          case NotificationTypes.reportDownloadReady:
            if (survey && element.identifier === this._selectedSelfSurvey$.value.id) {
              survey.hasReport = true;
              survey.updated = new Date();
              this._selectedSelfSurvey$.next(survey);
              this._hasReport$.next(true);
              console.log('Updated has report of ' + survey.moniker);
              console.log('Has Report: ' + survey.hasReport);
              console.log('Survey Status: ' + survey.status);
            }

            surveys.filter(s => s.id === element.identifier).forEach(sv => {
              sv.hasReport = true;
            });

            this._selfSurveys$.next(surveys);

            break;
          default:
            console.warn('unexpected Notification Type: ' + element.type);
        }
      });
    });
  }

  loadSummaries(
    pageSize: number,
    pageNumber: number,
    filter: string,
    sortItem: string,
    sortDirection: SortDirections
  ): void {
    if (!this._selfSurveys$.value) {
      const url =
        environment.apiConfig.baseUrl +
        environment.apiConfig.surveysApi +
        '?' +
        `pageSize=${pageSize}&pageNumber=${pageNumber}&filter=${filter}&sortItem=${sortItem}&sortDirection=${sortDirection}`;

      this.appService.setLoading(true);

      this.http
        .get<SurveySummaryModel[]>(url)
        .pipe(
          retryBackoff(environment.retryConfig),
          catchError(this._summariesError.bind(this))
        )
        .subscribe(result => {
          this._selfSurveys$.next(result);
          if (result && result.length > 0) {
            const currentSelection = this._selectedSelfSurvey$.value;
            if (currentSelection) {
              if (!result.find(s => s.id === currentSelection.id)) {
                result[0].updated = new Date();
                this._selectedSelfSurvey$.next(result[0]);
                console.log('Has Report: ' + result[0].hasReport);
                this._hasReport$.next(result[0].hasReport);
                this.responseService.loadResponse(result[0] && result[0].moniker);
              }
            } else {
              result[0].updated = new Date();
              this._selectedSelfSurvey$.next(result[0]);
              console.log('Has Report: ' + result[0].hasReport);
              this._hasReport$.next(result[0].hasReport);
              this.resultsService.loadSurveyResults(result[0].moniker, false);
              this.responseService.loadResponse(result[0] && result[0].moniker);
            }
          }
          this.appService.setLoading(false);
        });
    }
  }

  loadSurveyById(id: string): void {
    const url =
      environment.apiConfig.baseUrl +
      environment.apiConfig.surveysApi +
      `/${id}`;
      this.appService.setLoading(true);

      this.http
      .get<SurveyModel>(url)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._summaryError.bind(this))
      )
      .subscribe(result => {
        this.appService.setLoading(false);
        this._activeSurvey$.next(result);
      });
  }

  post(model: SurveyModel): Observable<SurveyModel> {
    const url =
      environment.apiConfig.baseUrl + environment.apiConfig.surveysApi;

    this._isAdding$.next(true);

    

      return this.http.post<SurveyModel>(url, model).pipe(
        retryBackoff(environment.retryConfig),
        tap(result => {

            if  (result.errorMessage && result.errorMessage.includes('ISBN')) {
              this._surveyError$.next(result.errorMessage);
            }
                
            this._selfSurveys$.next(null);
            this._activeSurvey$.next(result);
            this._isAdding$.next(false);
        }),
        catchError(this._modelError.bind(this))
        );
      
   
  }

  update(model: SurveyModel): Observable<boolean> {
    const url =
      environment.apiConfig.baseUrl +
      environment.apiConfig.surveysApi +
      `/${model.moniker || model.id}`;

    const patchModel = {
      operation: PatchOperations.replace,
      path: 'name',
      value: model.name
    } as PatchModel;

    return this.http.patch<SurveyModel>(url, patchModel).pipe(
      retryBackoff(environment.retryConfig),
      tap(result => {
        //this._selfSurveys$.next(null);
        this._activeSurvey$.next(result);
      }),
      switchMap(() => of(true)),
      catchError(this._updateError.bind(this))
    );
  }

  private _updateError(err) {
    this._handleError(err);
    return of(false);
  }

  private _summariesError(err) {
    this._handleError(err);
    return of(null as SurveySummaryModel[]);
  }

  private _summaryError(err) {
    this._handleError(err);
    return of(null as SurveySummaryModel);
  }

  private _modelError(err) {
    this._handleError(err);
    this._isAdding$.next(false);
    return of(null as SurveyModel);
  }

  private _handleError(err) {
    this.appService.setLoading(false);

    let error: ApplicationError;
    if (err && err instanceof HttpErrorResponse) {
      // A client-side or network error occurred. Handle it accordingly.
      let detailedMessage = `${err.message}`;
      if (err.statusText) {
        detailedMessage += `<br/>${err.statusText}`;
      }
      error = {
        errorMessage: this.translate.instant('errorMessageGenericBackend'),
        errorNumber: err.status,
        detailedMessage
      } as ApplicationError;
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      error = {
        errorMessage: this.translate.instant('errorMessageGenericBackend'),
        originalError: err
      } as ApplicationError;
    }
    this.appService.setLastError(error);

    this._selfSurveys$.next(null);
  }
}
